import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ClipboardProvider } from './context/ClipboardContext';
import { ConfigProvider } from './context/ConfigContext';

import Sidebar from './components/Sidebar';
import ExternalResourcesSidebar from './components/ExternalResourcesSidebar';

// --- EDITOR UNIFICADO ---
import EditorInforme from './pages/EditorInforme'; 

import './styles/quill-custom.css';

// --- PÁGINAS ---
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PatientsPage from './pages/PatientsPage';
import MedicalHistoryPage from './pages/MedicalHistoryPage';
import MedicalHistoryPage2 from './pages/MedicalHistoryPage2'; // Triage
import RecipesPage from './pages/RecipesPage';
import VademecumPage from './pages/VademecumPage';
import InformesPage from './pages/InformesPage'; 
import ReportsPage from './pages/ReportsPage'; // Reportes Admin
import ProcedureExecutionPage from './pages/ProcedureExecutionPage';
import RecipeEstudiosPage from './pages/RecipeEstudiosPage';
import SolicitudEstudiosPage from './pages/SolicitudEstudiosPage';
import ProcedimientosMedicosPage from './pages/ProcedimientosMedicosPage';
import ConfigurationPage from './pages/ConfigurationPage';
import MailboxPage from './pages/MailboxPage';
import AgendaPage from './pages/AgendaPage'; 
import AuditLogPage from './pages/AuditLogPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DocumentsPage from './pages/DocumentsPage'; 
import PagedHistory from './components/PagedHistory';
import StatisticsPage from './pages/StatisticsPage';

// ✅ IMPORTACIÓN NUEVA: PÁGINA DEL AGENTE CAPTURADOR
import AgentLauncherPage from './pages/AgentLauncherPage';

// --- IMPORTACIÓN DEL MICRÓFONO WHISPER ---
import FloatingMic from './components/FloatingMic';

// --- MÓDULO PARACLÍNICOS ---
import ParaclinicosPage from './pages/ParaclinicosPage';
import SolicitudParaclinicosPage from './pages/SolicitudParaclinicosPage';

// --- GESTIÓN DE USUARIOS ---
import UsersPage from './pages/UsersPage';
import NewUserPage from './pages/NewUserPage';
import EditUserPage from './pages/EditUserPage';
import AIAssistantPage from './pages/AIAssistantPage';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f4f6f8' },
  },
});

// --- DEFINICIÓN DE TIPOS PARA PROTECTED ROUTE ---
interface ProtectedRouteProps {
  children: React.ReactNode; 
  allowedRoles: string[];    
}

// --- COMPONENTE PROTECTED ROUTE ---
const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) return null; 

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppLayout = () => {
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Sidebar />
      <ExternalResourcesSidebar />
      
      <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', position: 'relative' }}>
        <Box sx={{ height: '64px' }} />
        <Outlet />
        
        {/* MICRÓFONO FLOTANTE GLOBAL */}
        <FloatingMic /> 
        
      </Box>
    </Box>
  );
};

// --- APP PRINCIPAL ---
function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <ConfigProvider>
            <ClipboardProvider>
              
              <Routes>
                {/* RUTAS PÚBLICAS */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                {/* BLOQUE PRINCIPAL PROTEGIDO */}
                <Route 
                  element={
                    <ProtectedRoute allowedRoles={['medico', 'administrador', 'asistente']}>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/agenda" element={<AgendaPage />} />
                  
                  {/* Pacientes */}
                  <Route path="/patients" element={<PatientsPage />} />
                  
                  {/* --- BLOQUE MÉDICO --- */}
                  <Route 
                    path="/medical-history/:patientId" 
                    element={
                      <ProtectedRoute allowedRoles={['medico', 'administrador']}>
                        <MedicalHistoryPage />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* --- BLOQUE ASISTENTE/TRIAGE --- */}
                  <Route 
                    path="/triage/:patientId" 
                    element={
                      <ProtectedRoute allowedRoles={['medico', 'administrador', 'asistente']}>
                        <MedicalHistoryPage2 />
                      </ProtectedRoute>
                    } 
                  />

                  {/* ✅ RUTA NUEVA: LANZADOR DEL AGENTE DE CAPTURA */}
                  <Route 
                    path="/agent-launcher" 
                    element={
                      <ProtectedRoute allowedRoles={['medico', 'administrador']}>
                        <AgentLauncherPage />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* --- RUTAS GENERALES CLÍNICAS --- */}
                  <Route path="/paraclinicos" element={<ParaclinicosPage />} />
                  <Route path="/paraclinicos/solicitud" element={<SolicitudParaclinicosPage />} />
                  <Route path="/recipes" element={<RecipesPage />} /> 
                  <Route path="/recipe-studies" element={<RecipeEstudiosPage />} />
                  <Route path="/vademecum" element={<VademecumPage />} />
                  <Route path="/procedimientos-medicos" element={<ProcedimientosMedicosPage />} />
                  <Route path="/solicitud-estudios" element={<SolicitudEstudiosPage />} />
                  <Route path="/procedures/execute" element={<ProcedureExecutionPage />} />
                  
                  {/* --- GESTIÓN DE INFORMES E HISTORIAL --- */}
                  {/* 1. Lista de Informes (Menú) */}
                  <Route path="/informes" element={<InformesPage />} />
                  <Route path="/informes/:patientId" element={<InformesPage />} />
				  
				  <Route path="/reporte-procedimientos" element={<ProcedureExecutionPage />} />
                  
                  {/* 2. EDITOR POTENTE (Sustituye al PagedDocument viejo) */}
                  <Route path="/editor-informe/:patientId" element={<EditorInforme />} />
                  
                  {/* 3. REDIRECCIÓN DE SEGURIDAD */}
                  <Route path="/nuevo-editor/:patientId" element={<Navigate to={`/editor-informe/${window.location.pathname.split('/').pop()}`} replace />} />
                  
                  {/* Editor de Historia Integral */}
                  <Route path="/reports/new/:patientId" element={<PagedHistory />} />
                  
                  {/* Documentos (Nube) */}
                  <Route path="/documents" element={<ProtectedRoute allowedRoles={['medico', 'administrador']}><DocumentsPage /></ProtectedRoute>} />

                  {/* --- ESTADISTICAS --- */}
				  <Route path="/stats" element={<StatisticsPage />} />

                  {/* --- CONFIGURACIÓN --- */}
                  <Route path="/config" element={<ConfigurationPage />} />
                  <Route path="/settings" element={<ConfigurationPage />} />
                  <Route path="/mailbox" element={<MailboxPage />} />
                  <Route path="/ai-assistant" element={<AIAssistantPage />} />
                  
                  {/* --- BLOQUE ADMIN --- */}
                  <Route path="/reports" element={<ProtectedRoute allowedRoles={['administrador']}><ReportsPage /></ProtectedRoute>} />
                  <Route path="/auditoria" element={<ProtectedRoute allowedRoles={['administrador']}><AuditLogPage /></ProtectedRoute>} />
                  <Route path="/users" element={<ProtectedRoute allowedRoles={['administrador']}><UsersPage /></ProtectedRoute>} />
                  <Route path="/users/new" element={<ProtectedRoute allowedRoles={['administrador']}><NewUserPage /></ProtectedRoute>} />
                  <Route path="/users/edit/:id" element={<ProtectedRoute allowedRoles={['administrador']}><EditUserPage /></ProtectedRoute>} />

                </Route>

                {/* Catch-All para rutas no existentes */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </ClipboardProvider>
          </ConfigProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;